import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Select,
  Tag,
  Modal,
  Form,
  Checkbox,
  message,
  Typography,
  Row,
  Col,
  Descriptions,
  DatePicker,
  Divider,
  Alert,
  Spin,
  Timeline,
  Empty,
  Tooltip,
} from 'antd';
import {
  EyeOutlined,
  EditOutlined,
  FileTextOutlined,
  ArrowRightOutlined,
  CarOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  BankOutlined,
  ExclamationCircleOutlined,
  RedoOutlined,
  SyncOutlined,
  TruckOutlined,
} from '@ant-design/icons';
import {
  adminService,
  Quotation,
  QuotationDetail,
  QuotationStatus,
  QuotationRefundInfo,
  QuotationStatusHistoryEntry,
  DeliveryCompany,
} from '../../services/adminService';
import { downloadBlob, readBlobError } from '../../utils/fileDownload';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

// State machine: must match backend QuotationStateMachine.ts.
const FORWARD_NEXT: Partial<Record<QuotationStatus, QuotationStatus>> = {
  pending: 'processing',
  processing: 'shipped',
  shipped: 'completed',
};
const CANCELLABLE: ReadonlySet<QuotationStatus> = new Set([
  'pending',
  'processing',
  'shipped',
]);

const STATUS_LABELS: Record<QuotationStatus, string> = {
  pending: 'Pending',
  processing: 'Processing',
  shipped: 'Shipped',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const isQuotationStatus = (s: string): s is QuotationStatus =>
  s === 'pending' || s === 'processing' || s === 'shipped' || s === 'completed' || s === 'cancelled';

const getNextStatus = (current: string): QuotationStatus | null =>
  isQuotationStatus(current) ? FORWARD_NEXT[current] ?? null : null;

const canCancel = (current: string): boolean =>
  isQuotationStatus(current) && CANCELLABLE.has(current);

const canEditShipping = (current: string): boolean => current === 'shipped';

/**
 * The order's non-empty delivery address lines, in order. Mirrors
 * AdminController.joinAddress, which builds the same list for status emails.
 */
const getAddressLines = (quotation: QuotationDetail): string[] =>
  [quotation.webAddress1, quotation.webAddress2, quotation.webAddress3]
    .map((line) => (typeof line === 'string' ? line.trim() : ''))
    .filter(Boolean);

/** Trimmed value, or null when the field is blank/absent (legacy orders). */
const orNull = (value: string | null | undefined): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const NOT_PROVIDED = <Text type="secondary">Not provided</Text>;

type OrderListFilters = {
  search: string;
  status: string;
  // 'failed' lists only orders whose card refund failed (issue #171).
  refund: '' | 'failed';
  dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null;
  // Last-updated range on the order record (issue #181).
  updatedRange: [dayjs.Dayjs, dayjs.Dayjs] | null;
};

type OrderSort = {
  sortBy?: 'statusAge' | 'amount';
  sortOrder?: 'asc' | 'desc';
};

type OrderQueryParams = {
  search?: string;
  status?: string;
  refund?: 'failed';
  startDate?: string;
  endDate?: string;
  updatedStart?: string;
  updatedEnd?: string;
  sortBy?: 'statusAge' | 'amount';
  sortOrder?: 'asc' | 'desc';
};

/**
 * Filter/sort params shared by the order list and its CSV export, so an
 * export always covers the same orders as the screen (issue #181).
 */
export const orderFilterParams = (
  filters: OrderListFilters,
  sort: OrderSort
): OrderQueryParams => {
  const params: OrderQueryParams = {};
  if (filters.search) params.search = filters.search;
  if (filters.status) params.status = filters.status;
  if (filters.refund) params.refund = filters.refund;
  if (filters.dateRange) {
    params.startDate = filters.dateRange[0].format('YYYY-MM-DD');
    params.endDate = filters.dateRange[1].format('YYYY-MM-DD');
  }
  if (filters.updatedRange) {
    params.updatedStart = filters.updatedRange[0].format('YYYY-MM-DD');
    params.updatedEnd = filters.updatedRange[1].format('YYYY-MM-DD');
  }
  if (sort.sortBy && sort.sortOrder) {
    params.sortBy = sort.sortBy;
    params.sortOrder = sort.sortOrder;
  }
  return params;
};

// Blocked reasons that leave money with us until someone acts in the Razorpay
// dashboard — the admin must be warned, not congratulated.
const MANUAL_REFUND_REASONS: string[] = [
  'legacy_unlinked',
  'partially_refunded',
  'payment_unreadable',
  'refund_unverified',
  'razorpay_not_configured',
];

const QuotationManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState<OrderListFilters>({
    search: '',
    status: '',
    refund: '',
    dateRange: null,
    updatedRange: null,
  });
  const [sort, setSort] = useState<OrderSort>({});

  // Modals
  const [quotationDetailVisible, setQuotationDetailVisible] = useState(false);
  const [advanceModalVisible, setAdvanceModalVisible] = useState(false);
  const [shipModalVisible, setShipModalVisible] = useState(false);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState<QuotationDetail | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  // RefNo of the order whose refund is being retried, so only that row spins.
  const [retryingRefund, setRetryingRefund] = useState<string | null>(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [advanceTarget, setAdvanceTarget] = useState<QuotationStatus | null>(null);
  const [shipMode, setShipMode] = useState<'advance' | 'edit'>('advance');
  const [deliveryCompanies, setDeliveryCompanies] = useState<DeliveryCompany[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [refundInfo, setRefundInfo] = useState<QuotationRefundInfo | null>(null);
  const [refundInfoLoading, setRefundInfoLoading] = useState(false);
  // The order being marked as repaid by bank transfer (issue #171).
  const [manualRefundTarget, setManualRefundTarget] = useState<Quotation | null>(null);
  const [manualRefundSaving, setManualRefundSaving] = useState(false);

  const [advanceForm] = Form.useForm();
  const [shipForm] = Form.useForm();
  const [cancelForm] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [manualRefundForm] = Form.useForm();

  useEffect(() => {
    loadQuotations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, filters, sort]);

  const loadQuotations = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...orderFilterParams(filters, sort),
      };

      const response = await adminService.getQuotations(params);
      setQuotations(response.data);
      setStatusCounts(response.statusCounts ?? {});
      setPagination(prev => ({ ...prev, total: response.pagination.totalItems }));
    } catch (error) {
      console.error('Failed to load quotations:', error);
      message.error('Failed to load quotations');
    } finally {
      setLoading(false);
    }
  };

  // Export ALL orders matching the current filters (not just the current page)
  // to CSV, one row per order line with delivery address and item detail.
  // The server builds the file, so this is one request rather than paging the
  // list endpoint and then fetching items per order.
  const handleExport = async () => {
    setExporting(true);
    try {
      const params: Parameters<typeof adminService.exportOrdersCSV>[0] =
        orderFilterParams(filters, sort);

      const blob = await adminService.exportOrdersCSV(params);
      downloadBlob(blob, `orders-${dayjs().format('YYYYMMDD-HHmmss')}.csv`);
      message.success('Export completed');
    } catch (error) {
      console.error('Failed to export orders:', error);
      message.error(await readBlobError(error, 'Failed to export orders'));
    } finally {
      setExporting(false);
    }
  };

  const loadDeliveryCompanies = async () => {
    try {
      setCompaniesLoading(true);
      const list = await adminService.getActiveDeliveryCompanies();
      setDeliveryCompanies(list);
    } catch (error) {
      console.error('Failed to load delivery companies:', error);
      message.error('Failed to load delivery companies');
    } finally {
      setCompaniesLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleStatusFilter = (value: string) => {
    setFilters(prev => ({ ...prev, status: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleRefundFilter = (value: '' | 'failed') => {
    setFilters(prev => ({ ...prev, refund: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleDateRangeFilter = (dates: [dayjs.Dayjs, dayjs.Dayjs] | null) => {
    setFilters(prev => ({ ...prev, dateRange: dates }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleUpdatedRangeFilter = (dates: [dayjs.Dayjs, dayjs.Dayjs] | null) => {
    setFilters(prev => ({ ...prev, updatedRange: dates }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleViewQuotation = async (refNo: string) => {
    try {
      setLoading(true);
      const quotationDetail = await adminService.getQuotationDetails(refNo);
      setSelectedQuotation(quotationDetail);
      setQuotationDetailVisible(true);
    } catch (error) {
      console.error('Failed to load quotation details:', error);
      message.error('Failed to load quotation details');
    } finally {
      setLoading(false);
    }
  };

  const openAdvance = (quotation: Quotation) => {
    const next = getNextStatus(quotation.webStatus);
    if (!next) return;
    setSelectedQuotation(quotation as QuotationDetail);
    setAdvanceTarget(next);
    if (next === 'shipped') {
      setShipMode('advance');
      shipForm.resetFields();
      shipForm.setFieldsValue({
        trackingNumber: quotation.TrackingNumber ?? '',
        deliveryCompanyName: quotation.DeliveryCompanyName ?? undefined,
        notes: '',
        sendNotesToCustomer: false,
      });
      loadDeliveryCompanies();
      setShipModalVisible(true);
    } else {
      advanceForm.resetFields();
      advanceForm.setFieldsValue({ notes: '', sendNotesToCustomer: false });
      setAdvanceModalVisible(true);
    }
  };

  const openEditShipping = (quotation: Quotation) => {
    setSelectedQuotation(quotation as QuotationDetail);
    setAdvanceTarget('shipped');
    setShipMode('edit');
    shipForm.resetFields();
    shipForm.setFieldsValue({
      trackingNumber: quotation.TrackingNumber ?? '',
      deliveryCompanyName: quotation.DeliveryCompanyName ?? undefined,
      notes: '',
      sendNotesToCustomer: false,
    });
    loadDeliveryCompanies();
    setShipModalVisible(true);
  };

  const openCancel = (quotation: Quotation) => {
    setSelectedQuotation(quotation as QuotationDetail);
    cancelForm.resetFields();
    cancelForm.setFieldsValue({ notes: '', sendNotesToCustomer: false });
    setRefundInfo(null);
    setCancelModalVisible(true);
    // Fetch the wallet refund preview so the admin sees what will be credited
    // back before confirming the cancellation.
    setRefundInfoLoading(true);
    adminService
      .getQuotationRefundInfo(quotation.RefNo)
      .then(setRefundInfo)
      .catch(error => {
        console.error('Failed to load refund info:', error);
        setRefundInfo(null);
      })
      .finally(() => setRefundInfoLoading(false));
  };

  const submitAdvance = async () => {
    if (!selectedQuotation || !advanceTarget) return;
    try {
      const values = await advanceForm.validateFields();
      setActionLoading(true);
      await adminService.updateQuotationStatus(
        selectedQuotation.RefNo,
        advanceTarget,
        values.notes,
        { sendNotesToCustomer: values.sendNotesToCustomer === true }
      );
      message.success(`Order advanced to ${STATUS_LABELS[advanceTarget]}`);
      setAdvanceModalVisible(false);
      loadQuotations();
    } catch (error: any) {
      if (!error?.errorFields) {
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to update quotation status';
        message.error(msg);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const submitShip = async () => {
    if (!selectedQuotation) return;
    try {
      const values = await shipForm.validateFields();
      setActionLoading(true);
      if (shipMode === 'advance') {
        await adminService.updateQuotationStatus(
          selectedQuotation.RefNo,
          'shipped',
          values.notes,
          {
            sendNotesToCustomer: values.sendNotesToCustomer === true,
            trackingNumber: values.trackingNumber,
            deliveryCompanyName: values.deliveryCompanyName,
          }
        );
        message.success('Order marked as Shipped');
      } else {
        await adminService.updateQuotationShipping(
          selectedQuotation.RefNo,
          values.trackingNumber,
          values.deliveryCompanyName,
          {
            sendNotesToCustomer: values.sendNotesToCustomer === true,
            notes: values.notes,
          }
        );
        message.success('Shipping info updated');
      }
      setShipModalVisible(false);
      loadQuotations();
    } catch (error: any) {
      if (!error?.errorFields) {
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to update shipping info';
        message.error(msg);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const submitCancel = async () => {
    if (!selectedQuotation) return;
    try {
      const values = await cancelForm.validateFields();
      setActionLoading(true);
      const result = await adminService.updateQuotationStatus(
        selectedQuotation.RefNo,
        'cancelled',
        values.notes,
        { sendNotesToCustomer: values.sendNotesToCustomer === true }
      );
      const needsManualRefund =
        result.cardRefund?.status === 'skipped' &&
        MANUAL_REFUND_REASONS.includes(result.cardRefund.reason ?? '');
      if (result.refundFailed || needsManualRefund) {
        message.warning(result.message || 'Order cancelled, but the refund failed — please refund manually.');
      } else if (result.cardRefund?.status === 'initiated') {
        // The money hasn't landed yet, so don't dress it up as done.
        message.info(result.message || 'Order cancelled; refund on the way');
      } else {
        message.success(result.message || 'Order cancelled');
      }
      setCancelModalVisible(false);
      loadQuotations();
    } catch (error: any) {
      if (!error?.errorFields) {
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to cancel order';
        message.error(msg);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditEmail = () => {
    if (!selectedQuotation) return;
    emailForm.setFieldsValue({ email: selectedQuotation.webEmail });
    setEmailModalVisible(true);
  };

  const handleEmailSubmit = async () => {
    if (!selectedQuotation) return;
    try {
      const values = await emailForm.validateFields();
      setEmailLoading(true);
      await adminService.updateQuotationEmail(selectedQuotation.RefNo, values.email);
      message.success('Email updated successfully');
      setEmailModalVisible(false);
      setSelectedQuotation(prev => (prev ? { ...prev, webEmail: values.email } : null));
      loadQuotations();
    } catch (error) {
      console.error('Failed to update email:', error);
      message.error('Failed to update email');
    } finally {
      setEmailLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'blue',
      processing: 'orange',
      shipped: 'cyan',
      completed: 'purple',
      cancelled: 'red',
    };
    return colors[status?.toLowerCase()] || 'default';
  };

  const renderStatusTag = (status: string) => {
    const lower = (status || '').toLowerCase();
    const label = isQuotationStatus(lower) ? STATUS_LABELS[lower] : status;
    return <Tag color={getStatusColor(status)}>{label}</Tag>;
  };

  // Status/notes audit timeline shown in the order-detail modal (issue #63).
  const renderStatusHistory = (history?: QuotationStatusHistoryEntry[]) => {
    if (!history || history.length === 0) {
      return <Empty description="No status changes recorded yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }
    return (
      <Timeline
        items={history.map(h => {
          const changed = dayjs(h.ChangedAt).format('MMM D, YYYY h:mm A');
          const isEdit = h.FromStatus === h.ToStatus;
          return {
            color: getStatusColor(h.ToStatus),
            children: (
              <div>
                <div>
                  {isEdit ? (
                    // A cancelled order is only edited to record a bank-transfer
                    // repayment (issue #171); a shipped one, its shipping info.
                    <Text strong>
                      {h.ToStatus === 'cancelled' ? 'Refund repaid by bank transfer' : 'Shipping updated'}
                    </Text>
                  ) : (
                    <>
                      {h.FromStatus && (
                        <>
                          {renderStatusTag(h.FromStatus)}
                          <ArrowRightOutlined style={{ margin: '0 4px' }} />
                        </>
                      )}
                      {renderStatusTag(h.ToStatus)}
                    </>
                  )}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {changed}
                  {h.ChangedBy ? ` · by ${h.ChangedBy}` : ''}
                </Text>
                {h.Notes && (
                  <div style={{ marginTop: 4 }}>
                    <Text>{h.Notes}</Text>{' '}
                    {h.NotesEmailedToCustomer && (
                      <Tag color="green" style={{ marginLeft: 4 }}>
                        emailed to customer
                      </Tag>
                    )}
                  </div>
                )}
              </div>
            ),
          };
        })}
      />
    );
  };

  /**
   * Refund wording (issues #133/#134).
   *
   * "Refund failed" was read as the system giving up on a refund that might
   * really still be in progress. It never meant that — `failed` is only written
   * when Razorpay rejected the request, and an accepted refund sits in
   * `pending` for days while it settles — so what these add is the evidence:
   * when it was sent, when it should arrive, and, when it did fail, the
   * gateway's own reason.
   */
  const refundProcessingLabel = (record: Quotation): string => {
    const expected = record.RefundExpected;
    if (!expected) return 'Refund processing';
    const daysLeft = dayjs(expected.latest).startOf('day').diff(dayjs().startOf('day'), 'day');
    // Past the window and still pending: don't keep counting down to a date
    // that has been and gone, say it is overdue and let the admin chase it.
    if (daysLeft < 0) return 'Refund overdue';
    if (daysLeft === 0) return 'Refund due today';
    return `Refund processing · ${daysLeft}d`;
  };

  const refundInFlightHint = (record: Quotation): string => {
    const sent = record.RefundInitiatedAt
      ? `Sent to Razorpay on ${dayjs(record.RefundInitiatedAt).format('MMM D, YYYY h:mm A')}. `
      : '';
    const expected = record.RefundExpected;
    if (!expected) {
      return `${sent}Card refunds usually reach the customer within 5-7 working days.`;
    }
    const overdue = dayjs(expected.latest).isBefore(dayjs(), 'day');
    const window = `Expected to reach the customer between ${dayjs(expected.earliest).format(
      'MMM D'
    )} and ${dayjs(expected.latest).format('MMM D, YYYY')} (${expected.workingDaysMin}-${
      expected.workingDaysMax
    } working days, weekends excluded).`;
    return overdue
      ? `${sent}${window} That window has passed — check the refund in the Razorpay dashboard.`
      : `${sent}${window} The estimate is ours, not the bank's.`;
  };

  const refundFailureHint = (record: Quotation): string => {
    const when = record.RefundInitiatedAt
      ? ` (attempted ${dayjs(record.RefundInitiatedAt).format('MMM D, YYYY h:mm A')})`
      : '';
    const reason = record.RefundFailureReason
      ? `This system recorded a failed refund${when}: "${record.RefundFailureReason}"`
      : `This system recorded a failed refund${when}.`;
    // Deliberately does not claim the money is still with us: a refund can
    // exist at Razorpay even when the attempt errored here, which is what
    // happened on 3 Sep 2026 (issue #133). Retry checks before acting.
    return `${reason} Retry refund checks Razorpay first — if the refund did go through, the order is corrected instead of refunded twice.`;
  };

  /** Send a rejected refund to Razorpay again (issue #133). */
  const handleRetryRefund = async (record: Quotation) => {
    setRetryingRefund(record.RefNo);
    try {
      const { message: outcome } = await adminService.retryCardRefund(record.RefNo);
      message.success(outcome || 'Refund sent to Razorpay.');
      loadQuotations();
    } catch (error: any) {
      // The gateway's reason is the only thing that tells the admin what to do
      // next, so it is shown rather than swallowed behind "something failed".
      message.error(
        error?.response?.data?.message || error?.message || 'Could not retry the refund'
      );
      loadQuotations();
    } finally {
      setRetryingRefund(null);
    }
  };

  /**
   * Record that Dasoon repaid a failed refund by bank transfer (issue #171).
   * Takes Retry refund off the order, so the customer cannot be paid twice.
   */
  const openManualRefund = (record: Quotation) => {
    manualRefundForm.resetFields();
    setManualRefundTarget(record);
  };

  const submitManualRefund = async () => {
    if (!manualRefundTarget) return;
    try {
      const { remark } = await manualRefundForm.validateFields();
      setManualRefundSaving(true);
      const refNo = manualRefundTarget.RefNo;
      const outcome = await adminService.markRefundedByBankTransfer(refNo, remark.trim());
      message.success(outcome || 'Recorded as refunded by bank transfer.');
      setManualRefundTarget(null);
      loadQuotations();
      if (quotationDetailVisible && selectedQuotation?.RefNo === refNo) {
        handleViewQuotation(refNo);
      }
    } catch (error: any) {
      if (!error?.errorFields) {
        message.error(
          error?.response?.data?.message || error?.message || 'Could not record the bank transfer'
        );
      }
    } finally {
      setManualRefundSaving(false);
    }
  };

  const manualRefundHint = (record: Quotation): string => {
    const when = record.RefundedAt ? ` on ${dayjs(record.RefundedAt).format('MMM D, YYYY h:mm A')}` : '';
    const who = record.RefundMarkedBy ? ` by ${record.RefundMarkedBy}` : '';
    return `The card refund failed. Repaid by bank transfer, recorded${who}${when}. Remark: ${
      record.RefundRemark || '—'
    }`;
  };

  // Compact age string (e.g. "5d 3h", "2h", "12m"). Returns '—' if no anchor.
  const formatStatusAge = (since?: string | null): string => {
    if (!since) return '—';
    const ms = Date.now() - dayjs(since).valueOf();
    if (!Number.isFinite(ms) || ms < 0) return '—';
    const minutes = Math.floor(ms / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      const m = minutes % 60;
      return m ? `${hours}h ${m}m` : `${hours}h`;
    }
    const days = Math.floor(hours / 24);
    const h = hours % 24;
    return h ? `${days}d ${h}h` : `${days}d`;
  };

  // Stale > 7d → red, > 3d → orange, otherwise default. Terminal statuses
  // don't get an age tag at all (the clock isn't meaningful once an order
  // is completed or cancelled).
  const renderStatusAge = (since: string | null | undefined, status: string) => {
    const lower = (status || '').toLowerCase();
    if (lower === 'completed' || lower === 'cancelled') {
      return <Text type="secondary">—</Text>;
    }
    if (!since) return <Text type="secondary">—</Text>;
    const days = (Date.now() - dayjs(since).valueOf()) / 86_400_000;
    let color: string | undefined;
    if (days >= 7) color = 'red';
    else if (days >= 3) color = 'orange';
    return <Tag color={color}>{formatStatusAge(since)}</Tag>;
  };

  /**
   * What cancelling will do to the customer's money. Every path returns
   * something for a paid order — an order that can't be refunded automatically
   * is the case that most needs saying out loud, since otherwise the admin
   * cancels a paid order and assumes the refund happened.
   */
  const renderRefundAlert = () => {
    if (refundInfoLoading) {
      return (
        <div style={{ marginBottom: 16 }}>
          <Spin size="small" /> <Text type="secondary">Checking payment &amp; refund details…</Text>
        </div>
      );
    }
    const style = { marginBottom: 16 };

    // PaymentStatus, not PaymentMethod: the latter records intent at checkout, so
    // a wallet order whose debit failed still carries 'wallet' despite never
    // having been paid.
    const wasPaid = selectedQuotation?.PaymentStatus === 'paid';

    // The preview failed to load. If the order was paid, say so rather than
    // showing nothing — cancelling blind is the exact failure this guards.
    if (!refundInfo) {
      return wasPaid ? (
        <Alert
          type="warning"
          showIcon
          style={style}
          message="Couldn't check refund details"
          description="This order was paid, but the refund details could not be loaded. After cancelling, confirm the customer was refunded in the Razorpay dashboard or their wallet."
        />
      ) : null;
    }

    const money = `$${refundInfo.amount.toFixed(2)}`;

    // Preview loaded but reports nothing paid, while the order says it was —
    // don't let that disagreement pass silently.
    if (refundInfo.paymentMethod === 'none' && wasPaid) {
      return (
        <Alert
          type="warning"
          showIcon
          style={style}
          message="Couldn't confirm refund details"
          description="This order is recorded as paid, but no refund information could be determined. Check with Razorpay or the customer's wallet before assuming they have been refunded."
        />
      );
    }

    if (refundInfo.walletPaid) {
      return refundInfo.alreadyRefunded ? (
        <Alert
          type="info"
          showIcon
          style={style}
          message="Wallet already refunded"
          description={`${money} was already credited back to the customer's wallet for this order. No further refund will be made.`}
        />
      ) : (
        <Alert
          type="warning"
          showIcon
          style={style}
          message="Wallet refund on cancel"
          description={`This order was paid from the customer's wallet. Cancelling will refund ${money} back to their wallet.`}
        />
      );
    }

    if (!refundInfo.cardPaid) return null;

    if (refundInfo.refundable) {
      return (
        <Alert
          type="warning"
          showIcon
          style={style}
          message="Card refund on cancel"
          description={
            <>
              <div>
                This order was paid by card via Razorpay. Cancelling will refund {money} in full to
                the original card. Card refunds usually take 5-7 working days to appear on the
                customer's statement.
              </div>
              {/* Dasoon stays on T+2 settlement, so a refused refund is
                  expected, not exceptional (issue #171). */}
              <div style={{ marginTop: 8 }}>
                <Text strong>The refund may fail.</Text> Razorpay pays refunds only from its
                balance, which holds each payment less Razorpay's fee and is moved to the bank at
                every settlement. If the refund fails, repay the customer by bank transfer, then use{' '}
                <Text strong>Mark repaid</Text> on the order. Until then it is listed under Refund
                failed.
              </div>
            </>
          }
        />
      );
    }

    switch (refundInfo.blockedReason) {
      case 'legacy_unlinked':
        return (
          <Alert
            type="error"
            showIcon
            style={style}
            message="Manual refund required"
            description={`This order was paid by card but has no stored Razorpay payment ID, so it can't be refunded automatically. After cancelling, refund ${money} manually in the Razorpay dashboard.`}
          />
        );
      case 'partially_refunded':
        return (
          <Alert
            type="error"
            showIcon
            style={style}
            message="Partially refunded already"
            description="This payment has already been partially refunded, so no automatic refund will be made. Complete the refund manually in the Razorpay dashboard."
          />
        );
      case 'refund_unverified':
        return (
          <Alert
            type="error"
            showIcon
            style={style}
            message="Refund can't be confirmed with Razorpay"
            description={`Razorpay reports an amount refunded on this payment but holds no refund for it, so no automatic refund will be made. After cancelling, check the payment in the Razorpay dashboard and refund ${money} there if no refund is listed.`}
          />
        );
      case 'payment_unreadable':
        return (
          <Alert
            type="error"
            showIcon
            style={style}
            message="Couldn't verify this payment with Razorpay"
            description={`Razorpay could not be reached, so we can't tell what has already been refunded and won't refund automatically. After cancelling, check the payment in the Razorpay dashboard and refund ${money} if it hasn't been refunded already.`}
          />
        );
      case 'razorpay_not_configured':
        return (
          <Alert
            type="error"
            showIcon
            style={style}
            message="Razorpay is not configured"
            description={`This order was paid by card but Razorpay credentials are missing, so it can't be refunded automatically. Refund ${money} manually.`}
          />
        );
      case 'already_refunded':
        return (
          <Alert
            type="info"
            showIcon
            style={style}
            message="Card already refunded"
            description={`${money} has already been refunded to the customer's card for this order. No further refund will be made.`}
          />
        );
      case 'refund_pending':
        return (
          <Alert
            type="info"
            showIcon
            style={style}
            message="Card refund already in progress"
            description={`A refund of ${money} has already been sent to Razorpay for this order and is still settling. No second refund will be made.`}
          />
        );
      default:
        return null;
    }
  };

  const sortOrderForColumn = (
    key: 'statusAge' | 'amount'
  ): 'ascend' | 'descend' | undefined => {
    if (sort.sortBy !== key) return undefined;
    return sort.sortOrder === 'asc' ? 'ascend' : 'descend';
  };

  const columns = [
    {
      title: 'Ref No',
      dataIndex: 'RefNo',
      key: 'RefNo',
      width: 150,
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: 'Customer',
      dataIndex: 'TraderName',
      key: 'TraderName',
      width: 200,
    },
    {
      title: 'Email',
      dataIndex: 'webEmail',
      key: 'webEmail',
      width: 250,
      responsive: ['lg' as const],
    },
    {
      title: 'Date Issued',
      dataIndex: 'DateIssued',
      key: 'DateIssued',
      width: 120,
      responsive: ['md' as const],
      render: (date: string) => dayjs(date).format('MMM D, YYYY'),
    },
    {
      // When the order last changed — status, cancellation, refund or an
      // admin edit (issue #181). The server falls back to the entry/issue
      // date, so only a missing field renders '—'.
      title: 'Last Updated',
      dataIndex: 'LastUpdatedAt',
      key: 'LastUpdatedAt',
      width: 170,
      responsive: ['md' as const],
      render: (date: string | null | undefined) =>
        date ? dayjs(date).format('MMM D, YYYY h:mm A') : '—',
    },
    {
      // Who made the last recorded change (issue #181): the admin on the
      // newest audit-trail row, 'customer' for a payment received, 'system'
      // for a gateway refund event, '—' for an order untouched since creation.
      title: 'Updated By',
      dataIndex: 'LastUpdatedBy',
      key: 'LastUpdatedBy',
      width: 130,
      responsive: ['lg' as const],
      render: (by: string | null | undefined) => by || '—',
    },
    {
      title: 'Amount',
      dataIndex: 'TotalDueAmt',
      key: 'amount',
      width: 120,
      render: (amount: number) => `$${(Number(amount) || 0).toFixed(2)}`,
      sorter: true,
      sortOrder: sortOrderForColumn('amount'),
    },
    {
      title: 'GST',
      dataIndex: 'GSTAmt',
      key: 'GSTAmt',
      width: 100,
      responsive: ['lg' as const],
      render: (amount: number) => `$${(Number(amount) || 0).toFixed(2)}`,
    },
    {
      title: 'Payment',
      key: 'payment',
      width: 160,
      responsive: ['lg' as const],
      // A refund in flight is invisible otherwise — card refunds take days to
      // settle, and this is the only place an admin sees one still moving.
      // Each state says when the money should land, or why it didn't
      // (issues #133/#134).
      render: (_: unknown, record: Quotation) => (
        <Space size={4} wrap>
          {record.PaymentMethod === 'card' && <Tag color="blue">Card</Tag>}
          {record.PaymentMethod === 'wallet' && <Tag color="purple">Wallet</Tag>}
          {record.RefundStatus === 'pending' && (
            <Tooltip title={refundInFlightHint(record)}>
              <Tag color="orange">{refundProcessingLabel(record)}</Tag>
            </Tooltip>
          )}
          {record.RefundStatus === 'processed' && (
            <Tooltip
              title={
                record.RefundedAt
                  ? `Confirmed by Razorpay on ${dayjs(record.RefundedAt).format('MMM D, YYYY h:mm A')}`
                  : 'Confirmed by Razorpay.'
              }
            >
              <Tag color="green">Refunded</Tag>
            </Tooltip>
          )}
          {record.RefundStatus === 'failed' && (
            <Tooltip title={refundFailureHint(record)}>
              <Tag color="red">Refund failed</Tag>
            </Tooltip>
          )}
          {record.RefundStatus === 'manual' && (
            <Tooltip title={manualRefundHint(record)}>
              <Tag color="cyan">Refunded by bank transfer</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'webStatus',
      key: 'webStatus',
      width: 120,
      render: (status: string) => renderStatusTag(status),
    },
    {
      title: 'In Status',
      dataIndex: 'StatusChangedAt',
      key: 'statusAge',
      width: 130,
      sorter: true,
      sortOrder: sortOrderForColumn('statusAge'),
      render: (since: string | null | undefined, record: Quotation) =>
        renderStatusAge(since, record.webStatus),
    },
    {
      title: 'User ID',
      dataIndex: 'webUserID',
      key: 'webUserID',
      width: 120,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 320,
      fixed: 'right' as const,
      render: (_: unknown, record: Quotation) => {
        const next = getNextStatus(record.webStatus);
        return (
          <Space wrap>
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleViewQuotation(record.RefNo)}
            />
            {next && (
              <Button
                type="primary"
                size="small"
                icon={<ArrowRightOutlined />}
                onClick={() => openAdvance(record)}
              >
                {STATUS_LABELS[next]}
              </Button>
            )}
            {canEditShipping(record.webStatus) && (
              <Button
                size="small"
                icon={<CarOutlined />}
                onClick={() => openEditShipping(record)}
              >
                Edit shipping
              </Button>
            )}
            {canCancel(record.webStatus) && (
              <Button
                danger
                size="small"
                icon={<CloseCircleOutlined />}
                onClick={() => openCancel(record)}
              >
                Cancel
              </Button>
            )}
            {/* The customer's money is still taken until this succeeds, and
                the Razorpay dashboard was the only way back (issue #133). */}
            {record.RefundStatus === 'failed' && (
              <Button
                size="small"
                icon={<RedoOutlined />}
                loading={retryingRefund === record.RefNo}
                onClick={() => handleRetryRefund(record)}
              >
                Retry refund
              </Button>
            )}
            {record.RefundStatus === 'failed' && (
              <Button size="small" icon={<BankOutlined />} onClick={() => openManualRefund(record)}>
                Mark repaid
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <Title level={2}>Order Management</Title>
        </Col>
        <Col>
          <Space>
            <Button icon={<FileTextOutlined />} onClick={handleExport} loading={exporting}>Export</Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        {([
          { status: 'pending', label: 'Pending', icon: <ClockCircleOutlined />, color: '#1677ff' },
          { status: 'processing', label: 'Processing', icon: <SyncOutlined />, color: '#fa8c16' },
          { status: 'shipped', label: 'Shipped', icon: <TruckOutlined />, color: '#13c2c2' },
        ] as const).map(card => {
          const isActive = filters.status === card.status;
          return (
            <Col xs={24} sm={12} lg={6} key={card.status}>
              <Card
                hoverable
                onClick={() =>
                  handleStatusFilter(isActive ? '' : card.status)
                }
                style={{
                  borderColor: isActive ? card.color : undefined,
                  borderWidth: isActive ? 2 : 1,
                }}
                bodyStyle={{ padding: '16px 20px' }}
              >
                <Row align="middle" justify="space-between">
                  <Col>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {card.label}
                    </Text>
                    <div style={{ fontSize: 28, fontWeight: 600, lineHeight: 1.2 }}>
                      {statusCounts[card.status] ?? 0}
                    </div>
                  </Col>
                  <Col>
                    <span
                      style={{
                        fontSize: 28,
                        color: card.color,
                        display: 'inline-flex',
                      }}
                    >
                      {card.icon}
                    </span>
                  </Col>
                </Row>
              </Card>
            </Col>
          );
        })}
        {/* Cuts across statuses, so it filters on the refund, not webStatus.
            Each order stays here until repaid and marked (issue #171). */}
        <Col xs={24} sm={12} lg={6}>
          <Card
            hoverable
            onClick={() => handleRefundFilter(filters.refund === 'failed' ? '' : 'failed')}
            style={{
              borderColor: filters.refund === 'failed' ? '#ff4d4f' : undefined,
              borderWidth: filters.refund === 'failed' ? 2 : 1,
            }}
            bodyStyle={{ padding: '16px 20px' }}
          >
            <Row align="middle" justify="space-between">
              <Col>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Refund failed
                </Text>
                <div style={{ fontSize: 28, fontWeight: 600, lineHeight: 1.2 }}>
                  {statusCounts.refundFailed ?? 0}
                </div>
              </Col>
              <Col>
                <span style={{ fontSize: 28, color: '#ff4d4f', display: 'inline-flex' }}>
                  <ExclamationCircleOutlined />
                </span>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={12} lg={6}>
            <Input.Search
              placeholder="Search by ref no, customer name, email or tracking no..."
              allowClear
              onSearch={handleSearch}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} lg={4}>
            <Select
              placeholder="Status"
              allowClear
              style={{ width: '100%' }}
              value={filters.status || undefined}
              onChange={value => handleStatusFilter(value || '')}
            >
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="processing">Processing</Select.Option>
              <Select.Option value="shipped">Shipped</Select.Option>
              <Select.Option value="completed">Completed</Select.Option>
              <Select.Option value="cancelled">Cancelled</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <RangePicker
              style={{ width: '100%' }}
              onChange={handleDateRangeFilter as any}
              placeholder={['Issued from', 'Issued to']}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <RangePicker
              style={{ width: '100%' }}
              onChange={handleUpdatedRangeFilter as any}
              placeholder={['Updated from', 'Updated to']}
            />
          </Col>
          <Col xs={24} sm={12} lg={4}>
            <Button onClick={loadQuotations}>Refresh</Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={quotations}
          rowKey="RefNo"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} quotations`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({ ...prev, current: page, pageSize: pageSize || 20 }));
            },
          }}
          onChange={(_pag, _filters, sorter: any) => {
            const s = Array.isArray(sorter) ? sorter[0] : sorter;
            const key = s?.columnKey as string | undefined;
            const order = s?.order as 'ascend' | 'descend' | undefined;
            if ((key === 'statusAge' || key === 'amount') && order) {
              setSort({
                sortBy: key,
                sortOrder: order === 'ascend' ? 'asc' : 'desc',
              });
            } else {
              setSort({});
            }
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          scroll={{ x: 1630 }}
        />
      </Card>

      {/* Order Detail Modal */}
      <Modal
        title={`Order Details - ${selectedQuotation?.RefNo}`}
        open={quotationDetailVisible}
        onCancel={() => setQuotationDetailVisible(false)}
        footer={[
          <Button key="close" onClick={() => setQuotationDetailVisible(false)}>
            Close
          </Button>,
        ]}
        width={1000}
      >
        {selectedQuotation && (
          <div>
            <Descriptions bordered column={2} style={{ marginBottom: '16px' }}>
              <Descriptions.Item label="Ref No">{selectedQuotation.RefNo}</Descriptions.Item>
              <Descriptions.Item label="Status">
                {renderStatusTag(selectedQuotation.webStatus)}
              </Descriptions.Item>
              <Descriptions.Item label="Customer">{selectedQuotation.TraderName}</Descriptions.Item>
              <Descriptions.Item label="Email">
                <Space>
                  {selectedQuotation.webEmail}
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={handleEditEmail}
                  />
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Date Issued">
                {dayjs(selectedQuotation.DateIssued).format('MMM D, YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="User ID">{selectedQuotation.webUserID}</Descriptions.Item>
              {selectedQuotation.PromoCode && (
                <Descriptions.Item label="Promo Code">
                  <Tag color="success">{selectedQuotation.PromoCode}</Tag>
                </Descriptions.Item>
              )}
              {Number(selectedQuotation.DiscountAmt) > 0 && (
                <Descriptions.Item label="Discount">
                  <Text type="success">−${(Number(selectedQuotation.DiscountAmt) || 0).toFixed(2)}</Text>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Total Amount" span={2}>
                <Space direction="vertical" size={0}>
                  <Text strong style={{ fontSize: '16px' }}>
                    ${(Number(selectedQuotation.TotalDueAmt) || 0).toFixed(2)}
                  </Text>
                  {Number(selectedQuotation.DiscountAmt) > 0 && (
                    <Text type="success" style={{ fontSize: 12 }}>
                      Promo discount of ${(Number(selectedQuotation.DiscountAmt) || 0).toFixed(2)} applied
                    </Text>
                  )}
                </Space>
              </Descriptions.Item>
              {/*
                Refund state, for a cancelled card order. Only rendered when
                there is a refund to talk about, so ordinary orders don't grow
                an empty row (issues #133/#134).
              */}
              {selectedQuotation.RefundStatus && (
                <Descriptions.Item label="Refund" span={2}>
                  <Space direction="vertical" size={4}>
                    <Space size={8} wrap>
                      {selectedQuotation.RefundStatus === 'pending' && (
                        <Tag color="orange">{refundProcessingLabel(selectedQuotation)}</Tag>
                      )}
                      {selectedQuotation.RefundStatus === 'processed' && (
                        <Tag color="green">Refunded</Tag>
                      )}
                      {selectedQuotation.RefundStatus === 'failed' && (
                        <Tag color="red">Refund failed</Tag>
                      )}
                      {selectedQuotation.RefundStatus === 'manual' && (
                        <Tag color="cyan">Refunded by bank transfer</Tag>
                      )}
                      {Number(selectedQuotation.RefundAmount) > 0 && (
                        <Text>${(Number(selectedQuotation.RefundAmount) || 0).toFixed(2)}</Text>
                      )}
                      {selectedQuotation.RefundStatus === 'failed' && (
                        <Button
                          size="small"
                          icon={<RedoOutlined />}
                          loading={retryingRefund === selectedQuotation.RefNo}
                          onClick={() => handleRetryRefund(selectedQuotation)}
                        >
                          Retry refund
                        </Button>
                      )}
                      {selectedQuotation.RefundStatus === 'failed' && (
                        <Button
                          size="small"
                          icon={<BankOutlined />}
                          onClick={() => openManualRefund(selectedQuotation)}
                        >
                          Mark repaid
                        </Button>
                      )}
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {selectedQuotation.RefundStatus === 'failed'
                        ? refundFailureHint(selectedQuotation)
                        : selectedQuotation.RefundStatus === 'manual'
                        ? manualRefundHint(selectedQuotation)
                        : selectedQuotation.RefundStatus === 'pending'
                        ? refundInFlightHint(selectedQuotation)
                        : selectedQuotation.RefundedAt
                        ? `Confirmed by Razorpay on ${dayjs(selectedQuotation.RefundedAt).format(
                            'MMM D, YYYY h:mm A'
                          )}.`
                        : 'Confirmed by Razorpay.'}
                    </Text>
                  </Space>
                </Descriptions.Item>
              )}
            </Descriptions>

            {/*
              Delivery details. Always rendered — an order with no address is
              exactly the case the admin needs to see, so the section shows
              "Not provided" rather than collapsing away.
            */}
            <Divider />
            <Title level={4}>Delivery</Title>
            <Descriptions bordered column={2} style={{ marginBottom: '16px' }}>
              <Descriptions.Item label="Recipient">
                {orNull(selectedQuotation.webDelName) ?? NOT_PROVIDED}
              </Descriptions.Item>
              <Descriptions.Item label="Contact Number">
                {orNull(selectedQuotation.webTel) ?? NOT_PROVIDED}
              </Descriptions.Item>
              <Descriptions.Item label="Delivery Address" span={2}>
                {getAddressLines(selectedQuotation).length > 0 ? (
                  <Space direction="vertical" size={0}>
                    {getAddressLines(selectedQuotation).map((line, index) => (
                      <Text key={index}>{line}</Text>
                    ))}
                  </Space>
                ) : (
                  NOT_PROVIDED
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Courier">
                {orNull(selectedQuotation.DeliveryCompanyName) ?? NOT_PROVIDED}
              </Descriptions.Item>
              <Descriptions.Item label="Tracking #">
                {orNull(selectedQuotation.TrackingNumber) ?? NOT_PROVIDED}
              </Descriptions.Item>
              <Descriptions.Item label="Shipped At" span={2}>
                {selectedQuotation.ShippedAt
                  ? dayjs(selectedQuotation.ShippedAt).format('MMM D, YYYY h:mm A')
                  : NOT_PROVIDED}
              </Descriptions.Item>
            </Descriptions>

            {selectedQuotation.items && selectedQuotation.items.length > 0 && (
              <div>
                <Divider />
                <Title level={4}>Items</Title>
                <Table
                  size="small"
                  dataSource={selectedQuotation.items}
                  rowKey="LineNO"
                  pagination={false}
                  scroll={{ x: 800 }}
                  columns={[
                    { title: 'Line', dataIndex: 'LineNO', key: 'LineNO', width: 60 },
                    { title: 'Product Code', dataIndex: 'SupersedeNo', key: 'SupersedeNo', width: 120 },
                    { title: 'UOM', dataIndex: 'UOM', key: 'UOM', width: 80 },
                    {
                      title: 'Description',
                      dataIndex: 'Description',
                      key: 'Description',
                      width: 300,
                      ellipsis: true,
                    },
                    {
                      title: 'Qty',
                      dataIndex: 'Qty',
                      key: 'Qty',
                      width: 80,
                      align: 'right' as const,
                    },
                    {
                      title: 'Unit Price',
                      dataIndex: 'Price',
                      key: 'Price',
                      width: 100,
                      align: 'right' as const,
                      render: (price: number) => `$${(Number(price) || 0).toFixed(2)}`,
                    },
                    {
                      title: 'Total',
                      dataIndex: 'ItemNetAmt',
                      key: 'ItemNetAmt',
                      width: 100,
                      align: 'right' as const,
                      render: (amount: number) => (
                        <Text strong>${(Number(amount) || 0).toFixed(2)}</Text>
                      ),
                    },
                  ]}
                />
              </div>
            )}

            <Divider />
            <Title level={4}>Status History</Title>
            {renderStatusHistory(selectedQuotation.statusHistory)}
          </div>
        )}
      </Modal>

      {/* Advance Status Modal (processing / completed) */}
      <Modal
        title={
          advanceTarget
            ? `Advance to ${STATUS_LABELS[advanceTarget]}`
            : 'Advance status'
        }
        open={advanceModalVisible}
        onOk={submitAdvance}
        onCancel={() => setAdvanceModalVisible(false)}
        confirmLoading={actionLoading}
        okText="Confirm"
      >
        <p>
          <Text>
            {selectedQuotation && advanceTarget && (
              <>
                Move <Text strong>{selectedQuotation.RefNo}</Text> from{' '}
                {renderStatusTag(selectedQuotation.webStatus)} to{' '}
                {renderStatusTag(advanceTarget)}.
              </>
            )}
          </Text>
        </p>
        <Form form={advanceForm} layout="vertical">
          <Form.Item name="notes" label="Notes (optional)">
            <TextArea rows={3} placeholder="Internal notes or message for the customer..." />
          </Form.Item>
          <Form.Item name="sendNotesToCustomer" valuePropName="checked">
            <Checkbox>Include these notes in the customer email</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      {/* Ship Modal — used both for advancing to shipped and for editing shipping info */}
      <Modal
        title={shipMode === 'advance' ? 'Mark as Shipped' : 'Edit Shipping Info'}
        open={shipModalVisible}
        onOk={submitShip}
        onCancel={() => setShipModalVisible(false)}
        confirmLoading={actionLoading}
        okText={shipMode === 'advance' ? 'Mark as Shipped' : 'Save & re-send email'}
      >
        {selectedQuotation && shipMode === 'advance' && (
          <p>
            <Text>
              Move <Text strong>{selectedQuotation.RefNo}</Text> from{' '}
              {renderStatusTag(selectedQuotation.webStatus)} to {renderStatusTag('shipped')}.
            </Text>
          </p>
        )}
        {selectedQuotation && shipMode === 'edit' && (
          <p>
            <Text type="secondary">
              Updating shipping info for <Text strong>{selectedQuotation.RefNo}</Text>. Saving will
              re-send the shipping email to the customer.
            </Text>
          </p>
        )}
        <Form form={shipForm} layout="vertical">
          <Form.Item
            name="trackingNumber"
            label="Tracking number"
            rules={[{ required: true, message: 'Tracking number is required' }]}
          >
            <Input maxLength={100} placeholder="e.g. SG1234567890" />
          </Form.Item>
          <Form.Item
            name="deliveryCompanyName"
            label="Delivery company"
            rules={[{ required: true, message: 'Please pick a delivery company' }]}
          >
            <Select
              loading={companiesLoading}
              placeholder="Select courier"
              options={deliveryCompanies.map(c => ({ value: c.Name, label: c.Name }))}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item name="notes" label="Notes (optional)">
            <TextArea rows={3} placeholder="Internal notes or a message for the customer..." />
          </Form.Item>
          <Form.Item name="sendNotesToCustomer" valuePropName="checked">
            <Checkbox>Include these notes in the customer email</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      {/* Cancel Modal */}
      <Modal
        title="Cancel Order"
        open={cancelModalVisible}
        onOk={submitCancel}
        onCancel={() => setCancelModalVisible(false)}
        confirmLoading={actionLoading}
        okText="Cancel order"
        okButtonProps={{ danger: true }}
        cancelText="Keep order"
      >
        <p>
          <Text>
            {selectedQuotation && (
              <>
                Cancel <Text strong>{selectedQuotation.RefNo}</Text> (currently{' '}
                {renderStatusTag(selectedQuotation.webStatus)})?
                <br />
                <Text type="secondary">This cannot be undone.</Text>
              </>
            )}
          </Text>
        </p>
        {selectedQuotation?.webStatus === 'shipped' && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message="This order has already shipped"
            description="The goods are already with the courier. Cancelling will still refund the customer in full — recover the stock separately."
          />
        )}
        {renderRefundAlert()}
        <Form form={cancelForm} layout="vertical">
          <Form.Item name="notes" label="Reason (optional)">
            <TextArea rows={3} placeholder="Why is the order being cancelled?" />
          </Form.Item>
          <Form.Item name="sendNotesToCustomer" valuePropName="checked">
            <Checkbox>Include this reason in the customer email</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      {/* Mark Repaid Modal (issue #171) */}
      <Modal
        title="Mark refund as repaid by bank transfer"
        open={manualRefundTarget !== null}
        onOk={submitManualRefund}
        onCancel={() => setManualRefundTarget(null)}
        confirmLoading={manualRefundSaving}
        okText="Mark repaid"
        destroyOnClose
      >
        {manualRefundTarget && (
          <>
            <p>
              <Text>
                The card refund of{' '}
                <Text strong>
                  ${(Number(manualRefundTarget.RefundAmount ?? manualRefundTarget.TotalDueAmt) || 0).toFixed(2)}
                </Text>{' '}
                for <Text strong>{manualRefundTarget.RefNo}</Text> failed.
              </Text>
            </p>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Only after the bank transfer has been made"
              description="Retry refund is removed from the order afterwards, so the customer is not paid twice. The customer sees the order as refunded by bank transfer. They do not see the remark."
            />
            <Form form={manualRefundForm} layout="vertical" preserve={false}>
              <Form.Item
                name="remark"
                label="Remark"
                rules={[
                  { required: true, whitespace: true, message: 'Enter the transfer date or reference' },
                  { max: 500, message: 'Remark must be 500 characters or less' },
                ]}
              >
                <TextArea rows={3} placeholder="e.g. Bank transfer on 22 Oct, ref 123456" />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* Email Edit Modal */}
      <Modal
        title="Update Order Email"
        open={emailModalVisible}
        onOk={handleEmailSubmit}
        onCancel={() => setEmailModalVisible(false)}
        confirmLoading={emailLoading}
      >
        <Form form={emailForm} layout="vertical">
          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input placeholder="customer@example.com" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default QuotationManagement;
